import type { RouteRecord } from "vite-react-ssg";
import { Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index";
import { blogCategories } from "@/data/blogCategories";
import { articleSlugs } from "@/data/articleSlugs";

const queryClient = new QueryClient();

const RootLayout = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ScrollToTop />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

// Helper: vite-react-ssg lazy route — defers the page's JS (and its image imports)
// so the home bundle stays minimal. SSG still prerenders each route to static HTML.
const lazy = (loader: () => Promise<{ default: React.ComponentType<unknown> }>) =>
  () => loader().then((m) => ({ Component: m.default }));

export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <Index />, entry: "src/pages/Index.tsx" },
      { path: "academy", lazy: lazy(() => import("./pages/Academy")), entry: "src/pages/Academy.tsx" },
      { path: "asd", lazy: lazy(() => import("./pages/Asd")), entry: "src/pages/Asd.tsx" },
      { path: "percorsi", lazy: lazy(() => import("./pages/Percorsi")), entry: "src/pages/Percorsi.tsx" },
      { path: "corsi/bambini", lazy: lazy(() => import("./pages/corsi/Bambini")), entry: "src/pages/corsi/Bambini.tsx" },
      { path: "corsi/ragazzi", lazy: lazy(() => import("./pages/corsi/Ragazzi")), entry: "src/pages/corsi/Ragazzi.tsx" },
      { path: "corsi/adulti", lazy: lazy(() => import("./pages/corsi/Adulti")), entry: "src/pages/corsi/Adulti.tsx" },
      { path: "corsi/agonismo", lazy: lazy(() => import("./pages/corsi/Agonismo")), entry: "src/pages/corsi/Agonismo.tsx" },
      { path: "dove-siamo/portuense", lazy: lazy(() => import("./pages/dove-siamo/Portuense")), entry: "src/pages/dove-siamo/Portuense.tsx" },
      { path: "dove-siamo/monteverde", lazy: lazy(() => import("./pages/dove-siamo/Monteverde")), entry: "src/pages/dove-siamo/Monteverde.tsx" },
      { path: "dove-siamo/bravetta", lazy: lazy(() => import("./pages/dove-siamo/Bravetta")), entry: "src/pages/dove-siamo/Bravetta.tsx" },
      { path: "dove-siamo/corviale", lazy: lazy(() => import("./pages/dove-siamo/Corviale")), entry: "src/pages/dove-siamo/Corviale.tsx" },
      { path: "dove-siamo/trullo", lazy: lazy(() => import("./pages/dove-siamo/Trullo")), entry: "src/pages/dove-siamo/Trullo.tsx" },
      { path: "maestri", lazy: lazy(() => import("./pages/Maestri")), entry: "src/pages/Maestri.tsx" },
      { path: "vita-in-palestra", lazy: lazy(() => import("./pages/VitaInPalestra")), entry: "src/pages/VitaInPalestra.tsx" },
      { path: "lezione-di-prova", lazy: lazy(() => import("./pages/LezioneProva")), entry: "src/pages/LezioneProva.tsx" },
      { path: "lezione-prova", element: <Navigate to="/lezione-di-prova" replace /> },
      { path: "stagione-2026-2027", lazy: lazy(() => import("./pages/StagioneIscrizioni")), entry: "src/pages/StagioneIscrizioni.tsx" },
      { path: "iscrizioni-2026-2027", element: <Navigate to="/stagione-2026-2027" replace /> },
      { path: "contatti", lazy: lazy(() => import("./pages/Contatti")), entry: "src/pages/Contatti.tsx" },
      { path: "blog", lazy: lazy(() => import("./pages/Blog")), entry: "src/pages/Blog.tsx" },
      {
        path: "blog/categoria/:slug",
        lazy: lazy(() => import("./pages/blog/Categoria")),
        entry: "src/pages/blog/Categoria.tsx",
        getStaticPaths: () => blogCategories.map((c) => `blog/categoria/${c.slug}`),
      },
      {
        path: "blog/:slug",
        lazy: lazy(() => import("./pages/ArticleDetail")),
        entry: "src/pages/ArticleDetail.tsx",
        getStaticPaths: () => articleSlugs.map((slug) => `blog/${slug}`),
      },
      { path: "privacy-policy", lazy: lazy(() => import("./pages/PrivacyPolicy")), entry: "src/pages/PrivacyPolicy.tsx" },
      { path: "cookie-policy", lazy: lazy(() => import("./pages/CookiePolicy")), entry: "src/pages/CookiePolicy.tsx" },
      { path: "*", lazy: lazy(() => import("./pages/NotFound")), entry: "src/pages/NotFound.tsx" },
    ],
  },
];

export default routes;
